import type { CatalogApprovalStatus, InventoryPurchase } from '../types/inventory';

/**
 * P-078 (E) · Lo que FRENA la aprobación de una compra y la UI ya sabe ANTES de tocar «Aprobar y confirmar»: el
 * proveedor o algún insumo de sus líneas que está pendiente de aprobación de catálogo, o que fue rechazado. El
 * backend sigue siendo la autoridad (`assertCatalogApproved`, 409 PURCHASE_CATALOG_PENDING): aquí solo se lee lo
 * que el detalle ya trae (`supplier.approvalStatus`, `lines[].ingredient.approvalStatus`) para explicarlo y ofrecer
 * el flujo que lo resuelve. Un `approvalStatus` ausente (backend anterior, proveedor resumido) NO cuenta como
 * pendiente ni como rechazado: no se afirma nada que no se sepa.
 */
export interface CatalogBlocker { kind: 'proveedor' | 'insumo'; id: string; name: string }
export interface PurchaseCatalogBlockers { pending: CatalogBlocker[]; rejected: CatalogBlocker[] }

/** El proveedor del detalle puede venir completo (con `approvalStatus`) o resumido (`{ id, name }`): sin estado, no se afirma nada. */
const statusOf = (value: { approvalStatus?: CatalogApprovalStatus } | { id: string; name: string } | null | undefined): CatalogApprovalStatus | undefined =>
  (value && 'approvalStatus' in value ? value.approvalStatus : undefined);

export function purchaseCatalogBlockers(purchase: Pick<InventoryPurchase, 'supplier' | 'lines'>): PurchaseCatalogBlockers {
  const pending: CatalogBlocker[] = [];
  const rejected: CatalogBlocker[] = [];
  const add = (status: CatalogApprovalStatus | undefined, blocker: CatalogBlocker) => {
    if (status === 'PENDING_APPROVAL') pending.push(blocker);
    else if (status === 'REJECTED') rejected.push(blocker);
  };
  if (purchase.supplier) add(statusOf(purchase.supplier), { kind: 'proveedor', id: purchase.supplier.id, name: purchase.supplier.name });
  const seen = new Set<string>();
  for (const line of purchase.lines ?? []) {
    if (seen.has(line.ingredient.id)) continue;
    seen.add(line.ingredient.id);
    add(statusOf(line.ingredient), { kind: 'insumo', id: line.ingredient.id, name: line.ingredient.name });
  }
  return { pending, rejected };
}

const list = (items: CatalogBlocker[]) => items.map((item) => `el ${item.kind} «${item.name}»`);
const joinAll = (parts: string[]) => (parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} y ${parts[parts.length - 1]}`);

/** «el proveedor «X» y el insumo «Y» están pendientes de aprobación de catálogo» (o el singular). */
export function describePendingBlockers(blockers: PurchaseCatalogBlockers): string | null {
  if (!blockers.pending.length) return null;
  const plural = blockers.pending.length > 1;
  return `${joinAll(list(blockers.pending))} ${plural ? 'están pendientes' : 'está pendiente'} de aprobación de catálogo`;
}

/** «el insumo «Z» fue rechazado» (o el plural). */
export function describeRejectedBlockers(blockers: PurchaseCatalogBlockers): string | null {
  if (!blockers.rejected.length) return null;
  const plural = blockers.rejected.length > 1;
  return `${joinAll(list(blockers.rejected))} ${plural ? 'fueron rechazados' : 'fue rechazado'}`;
}

/** Enlace de «Resolver ahora»: la bandeja de Aprobaciones con la compra a la que volver. */
export const approvalsReturnHash = (purchaseId: string) => `catalog-approvals/volver/purchases/${purchaseId}`;

/** Lee la compra a la que volver desde `#catalog-approvals/volver/purchases/<id>`; `null` si no se vino de una compra. */
export function purchaseToReturnTo(hash: string): string | null {
  const parts = hash.replace(/^#/, '').split('/');
  return parts[0] === 'catalog-approvals' && parts[1] === 'volver' && parts[2] === 'purchases' && parts[3] ? parts[3] : null;
}
